import type { SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormularioService, GlobalFormGroup, GlobalFormulario } from '@app/services/formulario';
import { NzTableComponent, NzTableQueryParams } from 'ng-zorro-antd/table';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { GeneralBreadCrumb } from '../general-template';

const FORM_BUSQUEDA: GlobalFormulario<{ busqueda: string }> = {
  datos: {
    busqueda: '',
  },
  updateOn: {
    busqueda: 'change',
  },
};

export interface Cabecera<T> {
  key: Exclude<keyof T, number | symbol> | 'acciones';
  texto: string;
  ordenar: boolean;
  clase?: string;
  visible?: boolean;
}
export interface KeyValueSort<T> {
  key: keyof T;
  value: 'ascend' | 'descend';
}

export interface ConfiguracionPaginaOrdenacion<T> {
  key: keyof T;
  tipo: 'ascend' | 'descend';
}
export interface ConfiguracionPagina<T> {
  busqueda: string;
  ordenacion: ConfiguracionPaginaOrdenacion<T>;
  pagina: number;
  total: number;
}
export class ConfiguracionPagina<T> {
  busqueda: string;
  ordenacion: ConfiguracionPaginaOrdenacion<T>;
  pagina: number;
  total: number;

  constructor(configuracion: Partial<ConfiguracionPagina<T>>) {
    this.busqueda = configuracion?.busqueda || '';
    this.ordenacion = configuracion?.ordenacion || { key: null, tipo: null };
    this.pagina = configuracion?.pagina || 1;
    this.total = configuracion?.total || 0;
  }

}
@Component({ standalone: false, selector: 'app-general-table-v2',
  templateUrl: 'general-table.component.html',
  styleUrls: ['../general-template.scss', './general-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeneralTableV2Component<T> implements OnInit, OnDestroy {
  @Input() breadcrumbs: GeneralBreadCrumb[] = [];
  @Input() titulo: string = '';
  @Input() cabecera: Cabecera<T>[] = [];
  cabeceraVisible: Cabecera<T>[] = [];
  formBusqueda: GlobalFormGroup<{ busqueda: string }>;

  // Indica si la paginación, ordenacion y busqueda se realiza en el servidor.
  @Input() datos: T[] = [];
  @Input() servidor: boolean;
  @Input() configuracionPagina: ConfiguracionPagina<T> = new ConfiguracionPagina<T>({});
  @Input() cargando: boolean;

  @Input() esError: boolean;
  @Input() tituloError: string;
  @Input() descripcionError: string;

  @Output() aplicarBusqueda = new EventEmitter<string>();
  @Output() aplicarOrdenacion = new EventEmitter<ConfiguracionPaginaOrdenacion<T>>();
  @Output() aplicarPaginacion = new EventEmitter<number>();
  @Output() recargarTabla = new EventEmitter<void>();
  @Output() dbClick = new EventEmitter<T>();

  @Input() tituloTemplate: TemplateRef<unknown>;
  @Input() cabeceraTemplate: TemplateRef<unknown>;
  @Input() filasTemplate: TemplateRef<unknown>;

  @ViewChild('componenteTabla') componenteTabla: NzTableComponent<T>;
  private readonly destroy$ = new Subject<void>();
  constructor(private readonly formularioService: FormularioService) {}
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.cabecera) this.cabeceraVisible = this.cabecera.filter((cabecera) => cabecera.visible !== false);
  }
  trackByRow = (index: number, row: T): unknown => (row as any)?.id ?? (row as any)?.key ?? index;
  trackByHeader = (index: number, header: Cabecera<T>): string | number => header.key || index;
  ngOnInit(): void {
    this.formBusqueda = this.formularioService.construirFormulario(FORM_BUSQUEDA);
    this.formBusqueda.controls.busqueda.setValue(this.configuracionPagina.busqueda);
    this.formBusqueda.controls.busqueda.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe((texto) => {
        this.aplicarBusqueda.emit(texto);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  onCambiarConfiguracionOrdenacion(params: NzTableQueryParams) {
    const keyValue = params.sort.find((keyValue) => keyValue.value !== null) as KeyValueSort<T>;
    let key: keyof T = null;
    let tipo: 'ascend' | 'descend' = null;
    if (keyValue !== undefined) {
      key = keyValue.key;
      tipo = keyValue.value;
    }
    if (key === this.configuracionPagina.ordenacion.key && tipo === this.configuracionPagina.ordenacion.tipo) return;
    this.aplicarOrdenacion.emit({ key, tipo });
  }
  onCambiarConfiguracionPagina(pageIndex: number) {
    this.aplicarPaginacion.emit(pageIndex);
  }
  onRecargarTabla() {
    this.recargarTabla.emit();
  }

  onDbClick(click: T) {
    this.dbClick.emit(click);
  }
}
